<template>
  <div class="flex flex-col my-4">
    <div class="flex items-center justify-end gap-2 mb-2" v-if="columns.length && legacy">
      <SelectMultipleField v-model="prefs.columns" :options="options" label="Table Columns"/>
    </div>

    <div class="overflow-x-auto">
      <PaginationNav v-if="hasTopNav" @flip="setPage" @limit-change="handleLimitChange"/>
      <div class="align-middle inline-block max-w-full min-w-full">
        <table class="min-w-full border-separate border-spacing-y-1" :class="{'table-fixed': hasCustomWidths}">
          <thead>
          <tr v-if="columns.length === 0" class="text-left text-sm font-normal text-bhav-blue-300 hidden sm:table-row">
            <slot name="headers" :rows="paginated ?? []"></slot>
          </tr>
          <Draggable v-else
                     v-model="prefs.columns"
                     tag="tr"
                     item-key="id"
                     class="text-left text-sm text-bhav-blue-300 hidden sm:table-row"
                     handle=".fak.fa-drag"
                     :animation="200">
            <template #item="{element}">
            <th :key="element"
                  :id="`column-${element}-header`"
                  class="group relative first:rounded-l-lg item last:rounded-r-lg bg-bhav-cool-gray-50 font-normal px-3 py-4 whitespace-nowrap transition-colors"
                  :class="[
                    {'cursor-pointer': getColumn(element)?.sort !== false},
                    getColumn(element)?.class
                  ]"
                  :style="prefs.getColumnWidth(element) ? {width: prefs.getColumnWidth(element) + 'px', minWidth: prefs.getColumnWidth(element) + 'px', maxWidth: prefs.getColumnWidth(element) + 'px'} : {}"
                  @click="sort(element)"
                  :title="getColumn(element)?.sort !== false ? 'Click to sort by this column or drag column to change ordering' : ''">

                <slot :name="`col-${element}-header`" :paginated="paginated">
                  <p :class="{'sr-only' : getColumn(element).show === false}" class="px-1">
                    <span class="fak fa-drag hover:cursor-grabbing !hidden group-hover:!inline" title=""></span>
                    <span v-if="getColumn(element).icon ?? false" :class="getColumn(element).icon"></span>
                    {{ getColumn(element).label }}
                    <span v-if="prefs?.sort?.column === element" :class="['fak', prefs?.sort?.dir === 'asc' ? 'fa-arrow-down' : 'fa-arrow-up']"></span>
                    <span class="fak fa-drag !inline opacity-0 group-hover:!hidden"></span>
                  </p>
                </slot>


                <div
                    @mousedown.stop="startResize($event, element)"
                    @dragstart.prevent.stop
                    class="resize-handle absolute inset-y-2 right-0 w-2 z-10 cursor-col-resize opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                    title="Drag to resize column">
                    <span class="w-0.5 h-full bg-bhav-blue-300 hover:bg-bhav-blue-400 transition-colors"></span>
                </div>
              </th>
            </template>
          </Draggable>
          </thead>
          <tbody>
          <template v-if="paginated.length">
            <tr v-for="item, i in paginated" :key="`item-${i}`" class="text-bhav-blue-500 group">
              <slot name="columns" :row="item" :i="i">
                <td v-for="column, k in selected" :key="`column-${k}`" :id="`column-${column.name}-${i}`" class="first:rounded-l-lg last:rounded-r-lg block sm:table-cell text-sm leading-5 group-hover:bg-bhav-cool-gray-50 px-3 py-4" :class="[column.class, { 'allow-overflow': column.allowOverflow }]" :style="prefs.getColumnWidth(column.name) ? {width: prefs.getColumnWidth(column.name) + 'px', minWidth: prefs.getColumnWidth(column.name) + 'px', maxWidth: prefs.getColumnWidth(column.name) + 'px'} : {}">
                  <p class="py-4 -mb-4 font-bold sm:hidden">{{ column.label }}</p>
                  <div class="px-1">
                    <slot :name="`col-${column.name}`" :row="item" :i="i"></slot>
                  </div>
                </td>
              </slot>
            </tr>
          </template>
          <tr v-else>
            <td :colspan="columns?.length || $slots.headers([])?.length || 1" class="block sm:table-cell text-sm  text-bhav-blue-500 leading-5 px-6 py-4">
              {{ emptyMessage }}
            </td>
          </tr>
          </tbody>
        </table>

      </div>
    </div>
    <PaginationNav @flip="setPage" @limit-change="handleLimitChange"/>
  </div>

  <RightDrawer v-if="!legacy" :open="columnsDrawer" title="Column Options" @close="closeDrawer">
    <ul class="flex flex-col gap-3">
      <li v-for="column,i in columns.filter(column => column?.show ?? true)" :key="`column-${i}`">
        <label :for="`select-${column.name}`" class="p-2 flex justify-between items-center  hover:bg-bhav-cool-gray-50 rounded-lg leading-4 cursor-pointer" :class="[prefs.columns.includes(column.name) ? 'text-bhav-blue-500' : 'text-bhav-blue-200']">
          <span class="text-sm">{{ column.label }}</span>
          <span class="fak" :class="[prefs.columns.includes(column.name) ? 'fa-shown' : 'fa-hidden']"></span>
        </label>
        <input type="checkbox" class="sr-only" :id="`select-${column.name}`" v-model="prefs.columns" :value="column.name"/>
      </li>
    </ul>
  </RightDrawer>
</template>

<script>
import { computed } from 'vue';
import PaginationNav from './PaginationNav.vue';
import { mapActions, mapState } from 'pinia';
import { usePrefsStore } from '@/stores/preferences';
import { SelectMultipleField } from './form';
import { useAuthStore } from '@/stores/auth';
import Draggable from 'vuedraggable';
import RightDrawer from './RightDrawer.vue';

export default {
  setup() {
    return {
      useAuthStore: useAuthStore()
    }
  },
  data() {
    const defaultPrefs = this.useAuthStore.getTablePrefs(this.view, { columns: (this.columns || []).filter(column => column.default).map(column => column.name) });
    // Ensure columns array has no duplicates while preserving all properties (including width)
    if (defaultPrefs._columns) {
      const seen = new Set();
      defaultPrefs._columns = defaultPrefs._columns.filter(col => {
        if (seen.has(col.name)) {
          return false;
        }
        seen.add(col.name);
        return true;
      });
    }
    // Migrate widths from localStorage if not present in server preferences
    const hasWidthsInColumns = defaultPrefs._columns.some(col => col.width !== undefined);
    if (!hasWidthsInColumns) {
      const savedWidths = localStorage.getItem(`table-widths-${this.view}`);
      if (savedWidths) {
        const widthsObj = JSON.parse(savedWidths);
        Object.entries(widthsObj).forEach(([name, width]) => {
          defaultPrefs.setColumnWidth(name, width);
        });
        // Clean up localStorage after migration
        localStorage.removeItem(`table-widths-${this.view}`);
      }
    }

    // Fall back if the persisted sort column was removed or made non-sortable
    if (defaultPrefs.sort?.column) {
      const sortColumn = (this.columns || []).find(c => c.name === defaultPrefs.sort.column);
      if (!sortColumn || !sortColumn.sort) {
        const firstSortable = (this.columns || []).find(c => c.sort);
        defaultPrefs.sort.column = firstSortable?.name ?? null;
      }
    }

    return {
      page: 1,
      columnsDrawer: false,
      prefs: defaultPrefs,
      resizing: null,  // Track column being resized: {column, startX, startWidth}
      justResized: false,  // Prevent click-to-sort immediately after resize
      isReordering: false  // Prevent multiple saves during column reordering
    }
  },
  emits: [
    'closeDrawer',
    'page-change',
    'limit-change',
    'sort-change'
  ],
  methods: {
    ...mapActions(usePrefsStore, ['setPref']),
    setPage(direction) {
      if (this.serverSide) {
        // For server-side pagination, emit event
        let newPage;
        switch(direction) {
          case 'next' :
            newPage = this.currentPage + 1;
            break;
          case 'prev' :
            newPage = this.currentPage - 1;
            break;
          default:
            newPage = Number(direction);
            break;
        }
        newPage = Math.max(1, Math.min(newPage, this.max));
        this.$emit('page-change', newPage);
      } else {
        // Client-side pagination
        let newPage;
        switch(direction) {
          case 'next' :
            newPage = this.page + 1;
            break;
          case 'prev' :
            newPage = this.page - 1;
            break;
          default:
            newPage = Number(direction);
            break;
        }
        this.page = Math.max(1, Math.min(newPage, this.max));
      }
    },
    handleLimitChange(newLimit) {
      if (this.serverSide) {
        this.$emit('limit-change', newLimit);
      }
    },
    getColumn(key) {
      return this.columns.find(column => column.name === key) ?? false;
    },
    sort(column) {
      // Don't sort if we just finished resizing
      if (this.justResized) return;

      if(!this.getColumn(column)?.sort ?? false) return;
      this.prefs.sort = {
        column,
        dir: this.prefs.sort?.column === column && this.prefs.sort?.dir === 'asc' ? 'desc' : 'asc'
      }

      // Emit sort change for server-side sorting
      if (this.serverSide) {
        this.$emit('sort-change', this.prefs.sort.column, this.prefs.sort.dir);
      }
    },
    closeDrawer() {
      this.columnsDrawer = false
      this.$emit('closeDrawer');
    },
    startResize(event, columnName) {
      event.stopPropagation();  // Prevent click-to-sort

      const th = event.target.closest('th');
      // Use saved width if exists, otherwise measure the DOM element
      const savedWidth = this.prefs.getColumnWidth(columnName);
      const startWidth = savedWidth || th.getBoundingClientRect().width;

      this.resizing = {
        column: columnName,
        startX: event.clientX,
        startWidth: startWidth
      };

      // Add global mouse event listeners
      document.addEventListener('mousemove', this.handleResize);
      document.addEventListener('mouseup', this.endResize);

      // Prevent text selection during resize
      document.body.style.userSelect = 'none';
    },
    handleResize(event) {
      if (!this.resizing) return;

      const delta = event.clientX - this.resizing.startX;
      const newWidth = Math.round(Math.max(80, this.resizing.startWidth + delta));  // Min 80px

      // Update width in column object
      this.prefs.setColumnWidth(this.resizing.column, newWidth);
    },
    endResize() {
      if (!this.resizing) return;

      // Remove global event listeners
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.endResize);

      // Re-enable text selection
      document.body.style.userSelect = '';

      this.resizing = null;

      // Prevent click-to-sort from firing immediately after resize
      this.justResized = true;
      setTimeout(() => {
        this.justResized = false;
      }, 100);

      // Save preferences to server after resize completes
      this.useAuthStore.setTablePrefs(this.prefs);
    },
    getColumnWidth(columnName) {
      return this.prefs.getColumnWidth(columnName) || 'auto';
    }
  },
  computed: {
    ...mapState(usePrefsStore, ['limit']),
    hasCustomWidths() {
      return this.prefs._columns.some(col => col.width !== undefined);
    },
    currentPage() {
      if (this.serverSide && this.serverPagination) {
        return Math.floor(this.serverPagination.start / this.serverPagination.limit) + 1;
      }
      return this.page;
    },
    currentLimit() {
      if (this.serverSide && this.serverPagination) {
        return this.serverPagination.limit;
      }
      return this.limit;
    },
    totalCount() {
      if (this.serverSide && this.serverPagination) {
        return this.serverPagination.count;
      }
      return this.data.length;
    },
    max() {
      return Math.ceil(this.totalCount / this.currentLimit);
    },
    filtered() {
      const sortColumnName = this.prefs?.sort?.column ?? false;
      if (sortColumnName) {
        const sortDir = this.prefs?.sort?.dir === 'asc' ? 1 : -1;
        const sortColumn = this.columns.find(column => column.name === sortColumnName) ?? false;
        const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

        // Check if sortColumn exists and has a sort function
        if (sortColumn && typeof sortColumn.sort === 'function') {
          return [...this.data].sort((a,b) => {
            return collator.compare(sortColumn.sort(a), sortColumn.sort(b)) * sortDir;
          })
        }
      }
      return this.data;
    },
    paginated() {
      if (this.serverSide) {
        // Server already paginated the data
        return this.filtered;
      }
      let val = this.filtered?.slice(this.from, this.to) || [];
      return val;
    },
    from() {
      if (this.serverSide && this.serverPagination) {
        return this.serverPagination.start;
      }
      return (this.page * this.limit) - this.limit;
    },
    to() {
      if (this.serverSide && this.serverPagination) {
        return this.serverPagination.start + this.data.length;
      }
      let to = this.page * this.limit;
      return to <= this.data.length ? to : this.data.length;
    },
    options() {
      return this.columns
          .map(column => (
                  {
                    label: column.label,
                    value: column.name
                  }
              )
          )
    },
    selected() {
      return this.prefs.columns.map(pref => this.columns.find(column => column.name === pref)).filter(pref => pref !== undefined);
    },
  },
  props: {
    data: {
      type: Array,
      required: true,
      default: () => []
    },
    default: {
      type: Number,
      default: 25
    },
    view: {
      type: String,
      default: 'table'
    },
    columns: {
      type: Array,
      default: () => []
    },
    legacy: {
      type: Boolean,
      default: true
    },
    showColumns: {
      type: Boolean,
      default: false
    },
    emptyMessage: {
      type: String,
      default: 'There are currently no items.',
      required: false
    },
    serverSide: {
      type: Boolean,
      default: false
    },
    serverPagination: {
      type: Object,
      default: null
    },
    hasTopNav: {
      type: Boolean,
      default: false
    }
  },
  provide() {
    return {
      from: computed(() => this.from),
      to: computed(() => this.to),
      count: computed(() => this.totalCount),
      page: computed(() => this.currentPage)
    }
  },
  watch: {
    data: {
      handler() {
        // Only reset page for client-side pagination
        if (!this.serverSide) {
          this.page = 1;
        }
      },
      deep: true
    },
    prefs: {
      handler(newPrefs) {
        // Remove duplicates from columns array before saving
        if (newPrefs.columns) {
          const uniqueColumns = [...new Set(newPrefs.columns)];
          if (uniqueColumns.length !== newPrefs.columns.length) {
            this.prefs.columns = uniqueColumns;
            return; // Skip saving, will be called again with unique values
          }
        }

        // Only save to server if not currently resizing or reordering to avoid spamming API
        // Resize end and reorder end will trigger explicit saves
        if (!this.resizing && !this.isReordering) {
          this.useAuthStore.setTablePrefs(this.prefs);
        }
      },
      deep: true
    },
    'prefs.columns': {
      handler(newOrder, oldOrder) {
        // Prevent recursive updates
        if (this.isReordering) return;

        // When columns are reordered, ensure widths are preserved
        if (newOrder && oldOrder && newOrder.length === oldOrder.length) {
          // Check if this is just a reorder (same columns, different order)
          const isSameSet = newOrder.every(col => oldOrder.includes(col));
          if (isSameSet) {
            // Set flag to prevent multiple saves during reorder
            this.isReordering = true;

            try {
              // Reorder _columns to match new column order while preserving widths
              const reorderedColumns = newOrder.map((colName, index) => {
                const existingCol = this.prefs._columns.find(col => col.name === colName);
                if (existingCol) {
                  // Preserve all properties (including width) and update order
                  return { ...existingCol, order: index };
                }
                return { name: colName, order: index };
              });
              this.prefs._columns = reorderedColumns;
            } finally {
              // Always reset flag and save, even if there's an error
              this.$nextTick(() => {
                this.isReordering = false;
                this.useAuthStore.setTablePrefs(this.prefs);
              });

              // Safety fallback: ensure flag is reset after 100ms
              setTimeout(() => {
                if (this.isReordering) {
                  console.warn('isReordering flag was stuck, forcing reset');
                  this.isReordering = false;
                }
              }, 100);
            }
          }
        }
      },
      deep: false
    },
    showColumns: {
      handler(next) {
        this.columnsDrawer = next
      }
    }
  },
  components: {
    PaginationNav, SelectMultipleField, Draggable, RightDrawer
  },
  created() {
    // Get valid limit values from PaginationNav limits prop
    const validLimits = [12, 24, 48, 96, 192];

    // Reset to default if current limit is not in valid limits
    if(!validLimits.includes(this.limit)) {
      this.setPref('limit', this.default);
    }
    this.columnsDrawer = this.showColumns;
  },
  beforeUnmount() {
    // Clean up resize listeners if component unmounts during resize
    if (this.resizing) {
      document.removeEventListener('mousemove', this.handleResize);
      document.removeEventListener('mouseup', this.endResize);
      document.body.style.userSelect = '';
    }
  }
}
</script>

<style>
thead tr > * {
  @apply bg-bhav-cool-gray-50 font-normal;
}
thead tr > *:first-child {
  @apply rounded-l-lg;
}
thead tr > *:last-child {
  @apply rounded-r-lg;
}

/* Apply ellipsis to header text */
thead th p {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.table-fixed {
  table-layout: fixed;
}

/* Force ellipsis on table cells with custom widths */
.table-fixed tbody td {
  max-width: 0;
}

/* For non-flex content, force ellipsis */
tbody td:not(.allow-overflow) > div:not(.flex),
tbody td:not(.allow-overflow) a {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

/* For flex containers, allow children to shrink and overflow */
tbody td .flex {
  min-width: 0;
  overflow: hidden;
}

tbody td .flex > * {
  min-width: 0;
}

/* Apply ellipsis to text elements inside flex containers */
tbody td .flex div[class*="text-"],
tbody td .flex span,
tbody td .flex p {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

/* Apply ellipsis to divs inside anchors (for non-flex content) */
tbody td a > div:not(.flex) {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
</style>
